import { useRef, useState } from 'react';
import { FormField, FormLabel } from 'ui/forms';
import { isEqual, pickBy } from 'lodash';
import { Dropdown, PopoverContainer, PopoverMenu, Stack } from '@koob/margaret';
import { useField } from 'formik';
import ToggleSection from 'components/ToggleSection';
import RegionPopoverItem from 'components/RegionPopoverItem';
import { useApp } from 'hooks';
import styled, { css } from 'styled-components';
import { RoleEnum } from '@koob/enums';
import { getAllCountriesByDmcId } from 'api/node/countries';
import { useAsync } from 'react-async';
import { useDebounce } from 'react-use';
import { PER_PAGE_FOR_LIST } from 'constants';

const Trigger = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: #ffffff;
  box-shadow: 0 1px 2px 0 ${({ theme }) => theme.shadow};
  padding: ${({ theme }) => theme.spacing(0.25)}
    ${({ theme }) => theme.spacing(0.5)};
  border: 1px solid ${({ theme }) => theme.separator};
  border-radius: 8px;
  line-height: 15px;
  min-width: 176px;
  width: 100%;
  min-height: 36px;
  position: relative;
  transition: border-color 100ms ease;

  ${({ disabled }) =>
    Boolean(disabled) &&
    css`
      cursor: not-allowed;
      background-color: ${({ theme }) => theme.disabled};
      color: #6d6d6d;
    `}

  svg {
    margin-left: ${({ theme }) => theme.spacing(0.5)};
  }
`;

const SearchInput = styled.input`
  outline: none;
  border: 0;
  width: 100%;
`;

const formatRegionsToGroupsCountry = (countries) => {
  if (!countries) return [];

  return countries.map((country) => ({
    group: {
      displayName: country.title,
      id: country.id,
    },
    regions: country.regions.map((region) => ({
      label: region.title,
      value: region.id,
      countryId: country.id,
    })),
  }));
};

const CountryGroup = ({ group, regions, onToggleRegion }) => {
  const toggleRef = useRef();

  return (
    <ToggleSection ref={toggleRef} title={group.displayName || ''}>
      {regions.map((region) => (
        <RegionPopoverItem
          key={region.value}
          region={region}
          onClick={() => onToggleRegion(region)}
        />
      ))}
    </ToggleSection>
  );
};

const RegionsGroupsSelectField = ({
  label,
  placeholder,
  name,
  disabled,
  isAllCountries,
}) => {
  const { currentOrganizationId, currentUserRole } = useApp();
  const [{ value }, , { setValue }] = useField({ name });
  const [search, setSearch] = useState();
  const [debouncedSearch, setDebouncedSearch] = useState();
  const displayValue = search || (value && value[0]?.region.label) || '';
  useDebounce(
    () => {
      setDebouncedSearch(search);
    },
    500,
    [search],
  );
  const dmcId =
    RoleEnum.KOOB_ADMIN === currentUserRole ? undefined : currentOrganizationId;
  const filters = pickBy({
    dmcId,
    search: debouncedSearch,
    page: 1,
    perPage: isAllCountries ? undefined : PER_PAGE_FOR_LIST,
  });

  const { data: countriesData } = useAsync({
    promiseFn: getAllCountriesByDmcId,
    filters,
    watchFn: (props, prevProps) => !isEqual(props.filters, prevProps.filters),
  });

  const countries = countriesData?.data || [];

  const groups = formatRegionsToGroupsCountry(countries);

  const handleToggleRegion = (targetFacility) => {
    setValue([{ region: targetFacility }]);
    setSearch('');
  };

  const handleSearchChange = (e) => {
    const newValue = e.target.value;
    setSearch(newValue);

    if (newValue === '') {
      setValue(null);
    }
  };

  return (
    <FormField>
      {Boolean(label) && <FormLabel>{label}</FormLabel>}

      {!disabled && (
        <Stack direction="column" gutterSize={1} size="full" alignX="stretch">
          <Dropdown
            trigger={
              <Trigger disabled={disabled}>
                <SearchInput
                  value={displayValue}
                  onChange={handleSearchChange}
                  placeholder={placeholder}
                />
                <i
                  className="fa-light fa-chevron-down"
                  style={{ fontSize: 24 }}
                />
              </Trigger>
            }
          >
            <PopoverContainer style={{ maxHeight: '100px' }}>
              <PopoverMenu>
                {groups.map(
                  ({ group, regions }) =>
                    regions.length > 0 && (
                      <CountryGroup
                        key={group.id}
                        group={group}
                        regions={regions}
                        onToggleRegion={handleToggleRegion}
                      />
                    ),
                )}
              </PopoverMenu>
            </PopoverContainer>
          </Dropdown>
        </Stack>
      )}
    </FormField>
  );
};

export default RegionsGroupsSelectField;
